import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { cookie } from 'utils/cookies/cookies';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiX,
  FiUser,
  FiZap,
  FiMapPin,
  FiCalendar,
  FiTruck,
  FiCreditCard,
  FiArrowRight,
  FiGrid,
  FiFileText,
  FiLayers,
  FiCpu,
} from 'react-icons/fi';
import './GlobalSearch.scss';

const CMS_MODULES = [
  { name: 'Station Downtime Report', path: '/stationDownTimeReport', keywords: ['station downtime report', 'downtime report', 'downtime', 'station downtime', 'charger downtime'] },
  { name: 'Station Downtime Report (Summary)', path: '/stationDownTimeReportSummaryOnly', keywords: ['downtime summary', 'station downtime summary', 'downtime summary report'] },
  { name: 'Charger Uptime Report', path: '/uptimeReport', keywords: ['uptime report', 'uptime', 'charger uptime', 'station uptime'] },
  { name: 'Download Advance Report', path: '/downloadAdvanceReport', keywords: ['advance report', 'download advance report', 'advance'] },
  { name: 'Download Reports', path: '/downloadReports', keywords: ['download reports', 'reports', 'export reports'] },
  { name: 'Charging History Report', path: '/chargingHistoryReport', keywords: ['charging history report', 'history report', 'charging history'] },
  { name: 'Transaction Report', path: '/transactionReport', keywords: ['transaction report', 'transactions report'] },
  { name: 'Failed Sessions Report', path: '/failedSessions', keywords: ['failed sessions', 'failed sessions report', 'failed'] },
  { name: 'Missed Sessions Report', path: '/missedSessions', keywords: ['missed sessions', 'missed sessions report', 'missed'] },
  { name: 'Consolidated Invoice Report', path: '/consolidatedInvoiceReport', keywords: ['consolidated invoice report', 'consolidated invoice'] },
  { name: 'Billing & Invoices', path: '/billingInvoice', keywords: ['billing', 'invoices', 'billing invoice', 'invoice', 'payments'] },
  { name: 'Live Charging Sessions', path: '/liveChargingSessions', keywords: ['live sessions', 'charging sessions', 'active sessions', 'sessions'] },
  { name: 'Customer Management', path: '/customers', keywords: ['customers', 'users', 'customer list', 'customer management'] },
  { name: 'Fleet Management', path: '/fleets', keywords: ['fleets', 'fleet management', 'fleet list', 'fleet owners'] },
  { name: 'Driver Management', path: '/driverManagement', keywords: ['driver management', 'drivers'] },
  { name: 'Stations & Chargers', path: '/stations', keywords: ['stations', 'charging stations', 'station list'] },
  { name: 'Charger Status & Control', path: '/chargerStatus', keywords: ['charger status', 'status', 'chargers control'] },
  { name: 'RFID Card Management', path: '/rfidList', keywords: ['rfid', 'rfid cards', 'rfid list', 'tags', 'card'] },
  { name: 'RFID Groups', path: '/rfidGroup', keywords: ['rfid group', 'rfid groups'] },
  { name: 'Vehicle Management', path: '/vehicles', keywords: ['vehicles', 'evs', 'vehicle list', 'evcc'] },
  { name: 'Pre-Loaded Campaigns', path: '/preloadedCampaigns', keywords: ['preloaded campaigns', 'campaigns', 'preloaded'] },
  { name: 'User Group Management', path: '/userGroupManagement', keywords: ['user group', 'user group management', 'groups'] },
  { name: 'Offers Management', path: '/offers', keywords: ['offers', 'discounts', 'promotions'] },
  { name: 'Coupons Management', path: '/coupons', keywords: ['coupons', 'coupon codes'] },
  { name: 'Ratings & Reviews', path: '/ratingReview', keywords: ['ratings', 'reviews', 'customer reviews'] },
  { name: 'WAR Dashboard', path: '/warDashboard', keywords: ['war dashboard', 'maintenance', 'live chargers'] },
  { name: 'OCPI Settings', path: '/OCPI-Setting', keywords: ['ocpi', 'ocpi settings', 'cpo', 'emsp'] },
  { name: 'OCPI Partners', path: '/OCPI-Partner', keywords: ['ocpi partner', 'partner management'] },
  { name: 'Tag Management', path: '/tagmanagement', keywords: ['tag management', 'tags'] },
  { name: 'Account Delete Requests', path: '/accountDeleteRequest', keywords: ['account delete', 'delete requests'] },
  { name: 'Settings & Profile', path: '/setting', keywords: ['settings', 'profile', 'user settings', 'configuration'] },
];

const buildTenantUrl = (path, tenantId) => {
  if (!tenantId) return path;
  try {
    const urlObj = new URL(path, window.location.origin);
    urlObj.searchParams.set('tenant', tenantId);
    return urlObj.pathname + urlObj.search;
  } catch (e) {
    if (path.includes('tenant=')) {
      return path.replace(/tenant=[^&]*/, `tenant=${tenantId}`);
    }
    return path.includes('?') ? `${path}&tenant=${tenantId}` : `${path}?tenant=${tenantId}`;
  }
};

const GlobalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const { navigateTo } = useContext(NavContext) || {};
  const navigate = useNavigate();

  // Handles item click: Logs into the associated tenant (if different) and navigates automatically
  const handleItemClick = useCallback(
    async (item, basePath) => {
      setIsOpen(false);
      setSearchTerm('');
      setResults(null);

      // Extract associated tenant ID from item or nested properties
      const itemTenantId =
        (typeof item?.tenant === 'object' ? item?.tenant?._id : item?.tenant) ||
        (typeof item?.charging_station?.tenant === 'object'
          ? item?.charging_station?.tenant?._id
          : item?.charging_station?.tenant) ||
        (typeof item?.charger?.tenant === 'object' ? item?.charger?.tenant?._id : item?.charger?.tenant) ||
        (typeof item?.owner?.tenant === 'object' ? item?.owner?.tenant?._id : item?.owner?.tenant) ||
        (typeof item?.user?.tenant === 'object' ? item?.user?.tenant?._id : item?.user?.tenant);

      const currentTenantId = new URLSearchParams(window.location.search).get('tenant');

      if (itemTenantId && itemTenantId !== currentTenantId) {
        let loggedIn = Boolean(localStorage.getItem(`${itemTenantId}_token`));

        if (!loggedIn) {
          try {
            const token = cookie.get('token');
            const apiUrl = process.env.REACT_APP_API_URL || '';

            // Log in as support role for associated tenant
            const res = await axios.post(
              `${apiUrl}/auth/loginas-support-role`,
              { tenant: itemTenantId },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res?.data?.tokens?.access?.token) {
              const accessToken = res.data.tokens.access.token;
              const userRole = res.data.user?.role || 'admin';

              localStorage.setItem(`${itemTenantId}_token`, accessToken);
              localStorage.setItem(
                `${itemTenantId}_tenant_data`,
                JSON.stringify({ tenantId: itemTenantId, role: userRole })
              );
              loggedIn = true;
            }
          } catch (e) {
            console.warn('Tenant login-as attempt skipped/failed:', e);
          }
        }

        if (loggedIn || localStorage.getItem(`${itemTenantId}_token`)) {
          const targetUrl = buildTenantUrl(basePath, itemTenantId);
          window.location.href = targetUrl;
          return;
        } else {
          // Fallback: If login to target tenant fails and no token is present,
          // navigate without target tenant param to prevent 401 logout
          console.warn('Could not acquire tenant token, navigating with current session context');
          const fallbackPath = currentTenantId ? buildTenantUrl(basePath, currentTenantId) : basePath;
          if (navigateTo) {
            navigateTo(fallbackPath);
          } else if (navigate) {
            navigate(fallbackPath);
          } else {
            window.location.href = fallbackPath;
          }
          return;
        }
      }

      // Same tenant or no tenant -> standard navigation
      const finalPath = currentTenantId ? buildTenantUrl(basePath, currentTenantId) : basePath;

      if (navigateTo) {
        navigateTo(finalPath);
      } else if (navigate) {
        navigate(finalPath);
      } else {
        window.location.href = finalPath;
      }
    },
    [navigateTo, navigate]
  );

  const [searchedQuery, setSearchedQuery] = useState('');

  // Execute API Search on Enter or explicit trigger
  const performSearch = useCallback(async (queryToSearch) => {
    const trimmed = (queryToSearch || '').trim();
    if (!trimmed || trimmed.length < 2) {
      setResults(null);
      setSearchedQuery('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setIsOpen(true);
    setSearchedQuery(trimmed);

    try {
      const token = cookie.get('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const currentTenantId = new URLSearchParams(window.location.search).get('tenant');
      let searchUrl = `${apiUrl}/global-search?query=${encodeURIComponent(trimmed)}`;
      if (currentTenantId) {
        searchUrl += `&tenant=${encodeURIComponent(currentTenantId)}`;
      }
      const response = await axios.get(searchUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.data) {
        setResults(response.data.data);
      } else {
        setResults(null);
      }
    } catch (err) {
      console.error('Global search error:', err);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global Keyboard Shortcuts (Cmd+K / Ctrl+K / Esc)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Module / Page Search matching (ONLY displayed when logged into a specific tenant)
  const currentTenantId = new URLSearchParams(window.location.search).get('tenant');
  const isTenantLoggedIn = Boolean(currentTenantId);

  const matchedModules = (isTenantLoggedIn && searchedQuery && searchedQuery.length >= 2)
    ? CMS_MODULES.filter((mod) => {
        const q = searchedQuery.toLowerCase().trim();
        return (
          mod.name.toLowerCase().includes(q) ||
          mod.path.toLowerCase().includes(q) ||
          mod.keywords.some((k) => k.toLowerCase().includes(q))
        );
      })
    : [];

  const hasPerformedSearch = Boolean(searchedQuery && searchedQuery.length >= 2);

  const hasResults =
    matchedModules.length > 0 ||
    (results &&
      (results.customers?.length > 0 ||
        results.chargers?.length > 0 ||
        results.stations?.length > 0 ||
        results.bookings?.length > 0 ||
        results.vehicles?.length > 0 ||
        results.rfids?.length > 0 ||
        results.tenants?.length > 0 ||
        results.connectors?.length > 0));

  const isMac = typeof window !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return (
    <div className="global-search-container" ref={wrapperRef}>
      <div className={`global-search-input-wrapper ${isOpen ? 'focused' : ''}`}>
        <FiSearch
          className={`search-icon ${loading ? 'icon-searching' : ''}`}
          onClick={() => performSearch(searchTerm)}
          style={{ cursor: 'pointer' }}
          title="Click to search"
        />
        <input
          ref={inputRef}
          type="text"
          className="global-search-input"
          placeholder="Search Anything... (Press Enter ↵)"
          value={searchTerm}
          onChange={(e) => {
            const val = e.target.value;
            setSearchTerm(val);
            if (!val.trim()) {
              setResults(null);
              setSearchedQuery('');
            }
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              performSearch(searchTerm);
            }
          }}
          onFocus={() => setIsOpen(true)}
        />
        {searchTerm ? (
          <button
            className="clear-btn"
            onClick={() => {
              setSearchTerm('');
              setSearchedQuery('');
              setResults(null);
              inputRef.current?.focus();
            }}
            title="Clear search"
          >
            <FiX />
          </button>
        ) : (
          <span className="shortcut-badge" title="Keyboard Shortcut">
            {isMac ? '⌘K' : 'Ctrl+K'}
          </span>
        )}
      </div>

      {isOpen && (
        <div className="global-search-popup">
          {loading && matchedModules.length === 0 && (
            <div className="search-skeleton-container">
              <div className="skeleton-header">
                <div className="skeleton-pulse skeleton-icon"></div>
                <div className="skeleton-pulse skeleton-title"></div>
              </div>
              {[1, 2, 3].map((num) => (
                <div key={num} className="skeleton-item">
                  <div className="skeleton-text-group">
                    <div className="skeleton-pulse skeleton-line-title"></div>
                    <div className="skeleton-pulse skeleton-line-sub"></div>
                  </div>
                  <div className="skeleton-pulse skeleton-badge"></div>
                </div>
              ))}
            </div>
          )}

          {!loading && !hasPerformedSearch && (
            <div className="search-hint-state">
              <p className="hint-title">Quick Search</p>
              <p className="hint-desc">
                Type your query and press <strong>Enter ↵</strong> to search by <strong>Tenant/Company</strong>,{' '}
                <strong>Connector Standard</strong>, <strong>Region/City/State</strong>,{' '}
                <strong>Vehicle Make/Model</strong>, <strong>Booking ID</strong>, or <strong>Charger ID</strong>.
              </p>
            </div>
          )}

          {!loading && hasPerformedSearch && !hasResults && (
            <div className="search-empty-state">
              <p className="empty-text">No matching records found for &quot;{searchedQuery}&quot;</p>
            </div>
          )}

          {hasResults && (
            <div className="search-results-list">
              {/* Modules & Navigation Reports */}
              {matchedModules.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiGrid className="category-icon text-primary" />
                    <span>Modules & Navigation Reports</span>
                  </div>
                  {matchedModules.map((mod) => (
                    <div
                      key={mod.path}
                      className="search-result-item"
                      onClick={() => handleItemClick(mod, mod.path)}
                    >
                      <div className="item-main-info">
                        <span className="item-title">
                          <FiFileText className="me-1 text-info" /> {mod.name}
                        </span>
                        <span className="item-subtitle">Module Path: {mod.path}</span>
                      </div>
                      <div className="item-tags">
                        <span className="badge bg-primary">Navigation Page</span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tenants & Companies */}
              {results?.tenants?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiLayers className="category-icon text-warning" />
                    <span>Tenants & Companies</span>
                  </div>
                  {results.tenants.map((tnt) => (
                    <div
                      key={tnt._id}
                      className="search-result-item"
                      onClick={() => handleItemClick({ tenant: tnt._id }, `/dashboard?tenant=${tnt._id}`)}
                    >
                      <div className="item-main-info">
                        <span className="item-title">{tnt.name || tnt.operator_name || 'Tenant'}</span>
                        <span className="item-subtitle">
                          {tnt.operator_name ? `Operator: ${tnt.operator_name} ` : ''}
                          {tnt.ocpi_party_name ? `• OCPI: ${tnt.ocpi_party_name}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        <span className="badge bg-primary">Tenant Mode</span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Connector Types */}
              {results?.connectors?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiCpu className="category-icon text-info" />
                    <span>Connector Types</span>
                  </div>
                  {results.connectors.map((conn) => (
                    <div
                      key={conn._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(conn, `/stations`)}
                    >
                      <div className="item-main-info">
                        <span className="item-title">{conn.name || conn.connector_standard || 'Connector'}</span>
                        <span className="item-subtitle">
                          Standard: {conn.connector_standard || 'N/A'} • Mode: {conn.connector_mode || 'N/A'}
                        </span>
                      </div>
                      <div className="item-tags">
                        <span className="badge bg-info">{conn.connector_mode || 'Plug'}</span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Customers & Fleets */}
              {results?.customers?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiUser className="category-icon text-info" />
                    <span>Customers & Fleets</span>
                  </div>
                  {results.customers.map((cust) => {
                    const isFleet = cust.role === 'fleet_owner' || cust.role === 'fleet';
                    const redirectPath = isFleet
                      ? `/fleetProfile/${cust._id}`
                      : `/customerProfile/${cust._id}`;
                    return (
                      <div
                        key={cust._id}
                        className="search-result-item"
                        onClick={() => handleItemClick(cust, redirectPath)}
                      >
                        <div className="item-main-info">
                          <span className="item-title">
                            {cust.name || `${cust.first_name || ''} ${cust.last_name || ''}`.trim() || 'Customer'}
                          </span>
                          <span className="item-subtitle">
                            Phone: {cust.phone || 'N/A'} {cust.email ? `• ${cust.email}` : ''}
                          </span>
                        </div>
                        <div className="item-tags">
                          {cust.customer_gst && (
                            <span className="badge bg-secondary">GST: {cust.customer_gst}</span>
                          )}
                          <span className={`badge ${isFleet ? 'bg-primary' : 'bg-success'}`}>
                            {cust.role || 'customer'}
                          </span>
                          <FiArrowRight className="item-arrow" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Chargers */}
              {results?.chargers?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiZap className="category-icon text-warning" />
                    <span>Chargers</span>
                  </div>
                  {results.chargers.map((chg) => (
                    <div
                      key={chg._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(chg, `/charger/${chg._id}`)}
                    >
                      <div className="item-main-info">
                        <span className="item-title">
                          {chg.charger_id || chg.name || 'Charger'}
                        </span>
                        <span className="item-subtitle">
                          {chg.name ? `${chg.name} ` : ''}
                          {chg.charging_station?.name ? `• Station: ${chg.charging_station.name}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        {chg.external_evse_id && (
                          <span className="badge bg-dark">Ext EVSE: {chg.external_evse_id}</span>
                        )}
                        <span
                          className={`badge ${
                            chg.charger_status === 'Available'
                              ? 'bg-success'
                              : chg.charger_status === 'Charging'
                              ? 'bg-info'
                              : 'bg-secondary'
                          }`}
                        >
                          {chg.charger_status || chg.operational_status || 'Active'}
                        </span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Stations */}
              {results?.stations?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiMapPin className="category-icon text-danger" />
                    <span>Charging Stations</span>
                  </div>
                  {results.stations.map((stn) => (
                    <div
                      key={stn._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(stn, `/stations/${stn._id}`)}
                    >
                      <div className="item-main-info">
                        <span className="item-title">{stn.name || 'Station'}</span>
                        <span className="item-subtitle">
                          {stn.city ? `${stn.city}, ` : ''}
                          {stn.state ? `${stn.state} ` : ''}
                          {stn.region ? `• Region: ${stn.region}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        {stn.region && <span className="badge bg-info">{stn.region}</span>}
                        <span className="badge bg-primary">
                          {stn.operational_status || 'Active'}
                        </span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Bookings */}
              {results?.bookings?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiCalendar className="category-icon text-success" />
                    <span>Bookings & Transactions</span>
                  </div>
                  {results.bookings.map((bk) => (
                    <div
                      key={bk._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(bk, '/liveChargingSessions')}
                    >
                      <div className="item-main-info">
                        <span className="item-title">
                          Txn ID: {bk.transaction_id || bk._id}
                        </span>
                        <span className="item-subtitle">
                          {bk.charger?.charger_id ? `Charger: ${bk.charger.charger_id} ` : ''}
                          {bk.customer_user_booked?.name ? `• User: ${bk.customer_user_booked.name}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        {bk.idTag && <span className="badge bg-secondary">Tag: {bk.idTag}</span>}
                        <span className="badge bg-info">{bk.status || 'Booking'}</span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Vehicles */}
              {results?.vehicles?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiTruck className="category-icon text-primary" />
                    <span>Vehicles & EVCC IDs</span>
                  </div>
                  {results.vehicles.map((veh) => (
                    <div
                      key={veh._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(veh, '/vehicles')}
                    >
                      <div className="item-main-info">
                        <span className="item-title">
                          {veh.make ? `${veh.make} ` : ''}{veh.model || veh.name || 'Vehicle'}
                          {veh.rto_no ? ` (${veh.rto_no})` : ''}
                        </span>
                        <span className="item-subtitle">
                          {veh.evcatalog?.name ? `Catalog: ${veh.evcatalog.name} ` : ''}
                          {veh.evmasterdata?.name ? `• Brand: ${veh.evmasterdata.name}` : ''}
                          {veh.owner?.name ? ` • Owner: ${veh.owner.name}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        {veh.evcc_id && (
                          <span className="badge bg-info text-dark">EVCC: {veh.evcc_id}</span>
                        )}
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* RFID Cards */}
              {results?.rfids?.length > 0 && (
                <div className="search-category-section">
                  <div className="category-header">
                    <FiCreditCard className="category-icon text-purple" />
                    <span>RFID Cards</span>
                  </div>
                  {results.rfids.map((rfid) => (
                    <div
                      key={rfid._id}
                      className="search-result-item"
                      onClick={() => handleItemClick(rfid, '/rfidList')}
                    >
                      <div className="item-main-info">
                        <span className="item-title">
                          Code: {rfid.code || rfid.card_number || 'RFID'}
                        </span>
                        <span className="item-subtitle">
                          Card #: {rfid.card_number || rfid.code}
                          {rfid.user?.name ? ` • User: ${rfid.user.name}` : ''}
                        </span>
                      </div>
                      <div className="item-tags">
                        <span
                          className={`badge ${
                            rfid.status === 'Active' ? 'bg-success' : 'bg-danger'
                          }`}
                        >
                          {rfid.status || 'Active'}
                        </span>
                        <FiArrowRight className="item-arrow" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
